/**
 * Navigation Constants
 *
 * External links, legal links, and shared navigation items used across the app.
 */

import {
  Home,
  Compass,
  Star,
  Briefcase,
  Rss,
  Users,
  Trophy,
  Bell,
  Activity,
  Flag,
  Bot,
  type LucideIcon,
} from 'lucide-react';

export const EXTERNAL_LINKS = {
  docs: 'https://docs.dextoro.com',
  fees: '/how-it-works#earn-forever', // Links to How It Works page fees section
  twitter: 'https://x.com/dextoro',
  instagram: 'https://instagram.com/dextoro',
  youtube: 'https://www.youtube.com/@dextoro',
  tiktok: 'https://tiktok.com/@dextoroapp',
  email: 'mailto:support@dextoro.com',
  support: 'mailto:support@dextoro.com',
  status: 'https://status.dextoro.com',
} as const;

export const LEGAL_LINKS = {
  privacy: 'https://dextoro.com/privacy',
  terms: 'https://dextoro.com/terms',
} as const;

export const SOCIAL_LINKS = [
  { name: 'Twitter', href: EXTERNAL_LINKS.twitter, icon: 'twitter' },
  { name: 'Instagram', href: EXTERNAL_LINKS.instagram, icon: 'instagram' },
  { name: 'YouTube', href: EXTERNAL_LINKS.youtube, icon: 'youtube' },
  { name: 'TikTok', href: EXTERNAL_LINKS.tiktok, icon: 'tiktok' },
  { name: 'Email', href: EXTERNAL_LINKS.email, icon: 'email' },
] as const;

/** Primary navigation items shared between LeftRail and MobileDrawer */
export interface NavigationItem {
  icon: LucideIcon;
  label: string;
  path: string;
  /** Extra pathname prefixes that should also highlight this item as active.
   *  Used e.g. to keep Market Maker highlighted on /mm/:mintAddress detail pages. */
  activePaths?: string[];
  /** Optional badge text (e.g., "NEW") to highlight the item */
  badge?: string;
  /** Optional badge variant name (e.g., "live", "new") matching Badge component variants */
  badgeVariant?: string;
}

export const NAVIGATION_ITEMS: NavigationItem[] = [
  { icon: Home, label: 'Home', path: '/' },
  { icon: Compass, label: 'Explore', path: '/tokens' },
  { icon: Briefcase, label: 'Portfolio', path: '/portfolio' },
  { icon: Bot, label: 'Market Maker', path: '/market-maker', activePaths: ['/mm/'] },
  { icon: Star, label: 'Watchlist', path: '/watchlist' },
  { icon: Activity, label: 'Activity', path: '/activity' },
  { icon: Flag, label: 'Events', path: '/events' },
  { icon: Rss, label: 'Feed', path: '/feed' },
  { icon: Users, label: 'Following', path: '/following' },
  { icon: Trophy, label: 'Leaderboards', path: '/leaderboards' },
  { icon: Bell, label: 'Alerts', path: '/alerts' },
];

/** Resource links shown in MobileDrawer and BottomNav "More" sheet */
export const RESOURCE_LINKS = [
  { href: '/how-it-works', label: 'How It Works', isExternal: false },
  { href: EXTERNAL_LINKS.docs, label: 'Docs', isExternal: true },
  { href: EXTERNAL_LINKS.support, label: 'Support', isExternal: true },
  { href: EXTERNAL_LINKS.status, label: 'Status', isExternal: true },
];
